import React, { useMemo, useState } from 'react'
import {
  AlertCircle,
  Clock,
  Layers,
  MessageSquare,
  Percent,
  Receipt,
  RotateCcw,
  Trophy,
  UserPlus,
  Users,
  UserX,
  Wallet,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import DashboardAlertBanner from '@/components/dashboard/DashboardAlertBanner'
import DashboardFilters from '@/components/dashboard/DashboardFilters'
import DashboardSection from '@/components/dashboard/DashboardSection'
import MetricCard from '@/components/dashboard/MetricCard'
import DailySalesChart from '@/components/dashboard/DailySalesChart'
import DistributionDonut from '@/components/dashboard/DistributionDonut'
import FunnelAccordion from '@/components/dashboard/FunnelAccordion'
import RankingBars, { type RankingItem } from '@/components/dashboard/RankingBars'
import { useConnectionAlerts } from '@/hooks/useConnectionAlerts'
import { usePipelines } from '@/hooks/usePipelines'
import { useDashboardCrmMetrics, useDashboardCrmSnapshot } from '@/hooks/useDashboardCrm'
import { defaultDashboardPeriod, type DashboardPeriod } from '@/lib/dashboardPeriod'
import {
  formatCurrency,
  formatCurrencyCompact,
  formatNumber,
  formatPercent,
  isCurrencyAbbreviated,
} from '@/lib/dashboardFormat'

const UNASSIGNED_LABEL = 'Não informado'

const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<DashboardPeriod>(() => defaultDashboardPeriod())
  const [pipelineId, setPipelineId] = useState<string | null>(null)

  const alerts = useConnectionAlerts()
  const { pipelines, loading: pipelinesLoading } = usePipelines()
  const metrics = useDashboardCrmMetrics(period, pipelineId)
  const snapshot = useDashboardCrmSnapshot(pipelineId)

  const summary = metrics.data.summary
  const comparisonLabel = metrics.comparison.label

  const salesRanking = useMemo<RankingItem[]>(() => {
    const rows = metrics.data.won_by_user
    const totalValue = rows.reduce((sum, row) => sum + row.value, 0)

    return rows
      .slice()
      .sort((a, b) => b.value - a.value || b.count - a.count)
      .map((row) => ({
        id: row.user_id ?? UNASSIGNED_LABEL,
        label: row.name,
        primary: formatCurrencyCompact(row.value),
        secondary: `${formatNumber(row.count)} ${row.count === 1 ? 'venda' : 'vendas'}`,
        // Share of revenue, which is what the currency figure beside it represents
        pct: totalValue > 0 ? (row.value / totalValue) * 100 : 0,
      }))
  }, [metrics.data.won_by_user])

  const lossRanking = useMemo<RankingItem[]>(
    () =>
      metrics.data.lost_by_user.map((row) => ({
        id: row.user_id ?? UNASSIGNED_LABEL,
        label: row.name,
        primary: formatNumber(row.count),
        pct: row.pct,
      })),
    [metrics.data.lost_by_user],
  )

  const lossReasonSlices = useMemo(
    () =>
      metrics.data.loss_reasons.map((row) => ({
        id: row.reason_id ?? 'sem-motivo',
        label: row.name,
        value: row.count,
        pct: row.pct,
      })),
    [metrics.data.loss_reasons],
  )

  const flowSlices = useMemo(() => {
    const flow = metrics.data.period_flow
    const total = flow.opened + flow.won + flow.lost

    return [
      { id: 'open', label: 'Em aberto', value: flow.opened },
      { id: 'won', label: 'Ganhas', value: flow.won },
      { id: 'lost', label: 'Perdidas', value: flow.lost },
    ]
      .filter((slice) => slice.value > 0)
      .map((slice) => ({ ...slice, pct: total > 0 ? (slice.value / total) * 100 : 0 }))
  }, [metrics.data.period_flow])

  const chatRanking = useMemo<RankingItem[]>(() => {
    const { by_user: byUser, open_chats_total: total } = snapshot.data.operation

    return byUser.map((row) => ({
      id: row.user_id,
      label: row.name,
      primary: formatNumber(row.count),
      pct: total > 0 ? (row.count / total) * 100 : 0,
    }))
  }, [snapshot.data.operation])

  const openTotal = snapshot.data.open_total
  const pipelineTotal = snapshot.data.pipeline_total
  const isAllPipelines = pipelineId === null
  const funnelScopeLabel = isAllPipelines ? 'todos os funis' : 'este funil'
  // The breakdown is the point of this card: it is the number the user could not read
  // anywhere without adding up three figures spread across the page
  const pipelineTotalHelper =
    pipelineTotal.count > 0
      ? `${formatNumber(pipelineTotal.open)} em aberto · ${formatNumber(pipelineTotal.won)} ganhos · ${formatNumber(pipelineTotal.lost)} perdidos`
      : `Todas as oportunidades já criadas em ${funnelScopeLabel}`

  const priceCoverageHelper =
    openTotal.count > 0 && openTotal.count_with_price < openTotal.count
      ? `${formatNumber(openTotal.count_with_price)} de ${formatNumber(openTotal.count)} oportunidades com valor informado`
      : undefined

  const salesRankingNote = metrics.data.won_by_user.some((row) => !row.user_id)
    ? 'Vendas sem responsável informado aparecem como "Não informado".'
    : 'Participação na receita fechada no período.'

  const snapshotPipelineName = snapshot.data.pipeline?.name
  const funnelDescription =
    'Visão atual das oportunidades em aberto em cada etapa do funil, independentemente do período selecionado'

  const error = metrics.error || snapshot.error

  return (
    <div className="flex w-full flex-col gap-8 pb-10">
      {!alerts.loading && (
        <DashboardAlertBanner
          whatsappDisconnected={alerts.whatsappDisconnected}
          openaiMissing={alerts.openaiMissing}
        />
      )}

      <DashboardFilters
        period={period}
        onPeriodChange={setPeriod}
        pipelines={pipelines.map((pipeline) => ({ id: pipeline.id, name: pipeline.name }))}
        pipelineId={pipelineId}
        onPipelineChange={setPipelineId}
        pipelinesLoading={pipelinesLoading}
        comparisonLabel={comparisonLabel}
      />

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-red-200 bg-red-50 p-4 dark:border-red-500/30 dark:bg-red-500/10">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#DC2626]" />
            <div className="flex flex-col">
              <p className="text-[14px] font-medium text-foreground">
                Não foi possível carregar todas as métricas
              </p>
              <p className="text-[12px] text-muted-foreground">{error}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void metrics.refetch()
              void snapshot.refetch()
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Tentar novamente
          </Button>
        </div>
      )}

      <DashboardSection
        title="Resumo do período"
        description="Resultado comercial no período selecionado, comparado com a base equivalente anterior."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            label="Valor ganho"
            value={formatCurrencyCompact(summary.won_value)}
            icon={Wallet}
            accent="green"
            current={summary.won_value}
            previous={summary.prev.won_value}
            comparisonLabel={comparisonLabel}
            helper={
              isCurrencyAbbreviated(summary.won_value)
                ? formatCurrency(summary.won_value)
                : undefined
            }
            loading={metrics.loading}
          />
          <MetricCard
            label="Negócios ganhos"
            value={formatNumber(summary.won_count)}
            icon={Trophy}
            accent="green"
            current={summary.won_count}
            previous={summary.prev.won_count}
            comparisonLabel={comparisonLabel}
            loading={metrics.loading}
          />
          <MetricCard
            label="Ticket médio"
            value={formatCurrencyCompact(summary.avg_ticket)}
            icon={Receipt}
            accent="blue"
            current={summary.avg_ticket}
            previous={summary.prev.avg_ticket}
            comparisonLabel={comparisonLabel}
            helper="Explica se a receita mudou por preço ou por volume"
            loading={metrics.loading}
          />
          <MetricCard
            label="Taxa de conversão"
            value={formatPercent(summary.conversion_rate)}
            icon={Percent}
            accent="purple"
            current={summary.conversion_rate}
            previous={summary.prev.conversion_rate}
            comparisonLabel={comparisonLabel}
            helper="Ganhos sobre o total de negócios fechados"
            loading={metrics.loading}
          />
          <MetricCard
            label="Negócios perdidos"
            value={formatNumber(summary.lost_count)}
            icon={XCircle}
            accent="red"
            current={summary.lost_count}
            previous={summary.prev.lost_count}
            comparisonLabel={comparisonLabel}
            lowerIsBetter
            loading={metrics.loading}
          />
          <MetricCard
            label="Leads novos"
            value={formatNumber(summary.new_leads)}
            icon={UserPlus}
            accent="orange"
            current={summary.new_leads}
            previous={summary.prev.new_leads}
            comparisonLabel={comparisonLabel}
            helper="Topo de funil: separa queda de conversão de queda de entrada"
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title="Ganhos"
        description="Onde e com quem a receita do período foi fechada."
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <DailySalesChart data={metrics.data.daily} loading={metrics.loading} />
          </div>
          <RankingBars
            title="Vendas por atendente"
            description={salesRankingNote}
            items={salesRanking}
            tone="positive"
            emptyMessage="Nenhuma venda registrada neste período."
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title="Perdas"
        description="Entenda os principais motivos das perdas e quais atendentes registraram mais negócios perdidos."
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DistributionDonut
            title="Motivos de perda"
            description="Distribuição dos negócios perdidos no período"
            slices={lossReasonSlices}
            centerLabel="perdidos"
            emptyMessage="Nenhuma perda registrada neste período."
            loading={metrics.loading}
          />
          <RankingBars
            title="Perdas por atendente"
            description="Quantidade de negócios perdidos registrados por cada atendente."
            items={lossRanking}
            tone="negative"
            emptyMessage="Nenhuma perda registrada neste período."
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title="Funil"
        badge={isAllPipelines ? 'Todos os funis' : snapshotPipelineName}
        description={funnelDescription}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard
                label="Valor em aberto"
                value={formatCurrencyCompact(openTotal.value)}
                icon={Wallet}
                accent="blue"
                helper={
                  priceCoverageHelper ??
                  (isCurrencyAbbreviated(openTotal.value)
                    ? formatCurrency(openTotal.value)
                    : undefined)
                }
                loading={snapshot.loading}
              />
              <MetricCard
                label="Leads em aberto"
                value={formatNumber(openTotal.count)}
                icon={Layers}
                accent="slate"
                helper={`Soma das etapas de ${funnelScopeLabel}, sem ganhos e perdas`}
                loading={snapshot.loading}
              />
              <MetricCard
                label={isAllPipelines ? 'Total de leads' : 'Total de leads do funil'}
                value={formatNumber(pipelineTotal.count)}
                icon={Users}
                accent="purple"
                helper={pipelineTotalHelper}
                loading={snapshot.loading}
              />
            </div>
            <FunnelAccordion pipelines={snapshot.data.pipelines} loading={snapshot.loading} />
          </div>

          <DistributionDonut
            title="Movimentação no período"
            description="Composição dos negócios que se moveram no período selecionado"
            slices={flowSlices}
            centerLabel="negócios"
            emptyMessage="Nenhuma movimentação neste período."
            loading={metrics.loading}
            palette={['var(--chart-1)', 'var(--chart-2)', 'var(--chart-4)']}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title="Atendimentos"
        badge="posição atual"
        description="Acompanhe a situação dos atendimentos em tempo real e identifique rapidamente onde é preciso agir"
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <MetricCard
              label="Atendimentos abertos"
              value={formatNumber(snapshot.data.operation.open_chats_total)}
              icon={MessageSquare}
              accent="blue"
              loading={snapshot.loading}
            />
            <MetricCard
              label="Sem responsável"
              value={formatNumber(snapshot.data.operation.unassigned)}
              icon={UserX}
              accent="orange"
              helper="Leads que entraram e ninguém assumiu"
              loading={snapshot.loading}
            />
            <MetricCard
              label={`Sem resposta há mais de ${snapshot.data.operation.stale_hours}h`}
              value={formatNumber(snapshot.data.operation.awaiting_reply)}
              icon={Clock}
              accent="red"
              helper="Última mensagem foi do cliente"
              loading={snapshot.loading}
            />
          </div>
          <RankingBars
            title="Atendimentos por usuário"
            description="Conversas em aberto atribuídas a cada membro da equipe."
            items={chatRanking}
            tone="neutral"
            emptyMessage="Nenhum atendimento aberto atribuído no momento."
            loading={snapshot.loading}
            maxItems={8}
          />
        </div>
      </DashboardSection>
    </div>
  )
}

export default Dashboard
